<template>
  <div class="user-avatar-select">
    <!-- Avatar Button -->
    <div
      class="avatar-button"
      @click="toggleDropdown"
      @mouseenter="showTooltip = true"
      @mouseleave="showTooltip = false"
      :class="{ 'is-disabled': disabled }"
    >
      <!-- Selected User Avatar -->
      <div v-if="selectedUser" class="user-avatar" :class="avatarSizeClass">
        <BunnyImage
          :path="selectedUser.profile?.avatarPath || selectedUser.avatarPath"
          :fallback="selectedUser.profile?.avatar || selectedUser.avatar"
          :alt="selectedUser.name"
          img-class="avatar-image"
        >
          <template #placeholder>
            <div
              class="avatar-initials"
              :style="{ backgroundColor: selectedUser.color || getUserColor(selectedUser.email) }"
            >
              {{ getUserInitials(selectedUser.name || selectedUser.email) }}
            </div>
          </template>
        </BunnyImage>
        
        <!-- Lock indicator for admin users -->
        <span
          v-if="locked"
          class="avatar-locked"
          :title="lockedTitle"
        >
          <font-awesome-icon :icon="['fas', 'lock']" />
        </span>
        <!-- Clear button -->
        <button
          v-else-if="clearable && !disabled"
          class="avatar-clear"
          @click.stop="clearSelection"
          title="Clear assignment"
        >
          <font-awesome-icon :icon="['fas', 'times']" />
        </button>
        <!-- Warning badge -->
        <span
          v-if="warning"
          class="avatar-warning"
          :title="warning"
        >
          <font-awesome-icon :icon="['fas', 'exclamation']" />
        </span>
      </div>
      
      <!-- Empty State -->
      <div v-else class="avatar-placeholder" :class="avatarSizeClass">
        <span class="placeholder-icon">
          <font-awesome-icon :icon="['fal', 'plus']" />
        </span>
      </div>
      
      <!-- Tooltip for selected user -->
      <div v-if="selectedUser && !isOpen" class="avatar-tooltip" v-show="showTooltip">
        {{ selectedUser.name || selectedUser.email }}
      </div>
    </div>

    <!-- SearchableSelect Dropdown -->
    <SearchableSelect
      v-show="false"
      ref="searchableSelect"
      v-model="internalValue"
      :items="filteredUsers"
      value-field="id"
      display-field="displayName"
      placeholder="Select user..."
      search-placeholder="Search users..."
      no-results-text="No users found"
      @select="handleUserSelect"
    >
      <template #item="{ item }">
        <div class="is-flex is-align-items-center">
          <!-- User Avatar -->
          <div class="user-list-avatar mr-3">
            <BunnyImage
              :path="item.profile?.avatarPath || item.avatarPath"
              :fallback="item.profile?.avatar || item.avatar"
              :alt="item.name"
              img-class="avatar-image"
            >
              <template #placeholder>
                <div class="avatar-initials" :style="{ backgroundColor: getUserColor(item.email) }">
                  {{ getUserInitials(item.name || item.email) }}
                </div>
              </template>
            </BunnyImage>
          </div>
          
          <!-- User Info -->
          <div class="is-flex-grow-1">
            <div class="has-text-weight-medium">{{ item.name || 'Unnamed User' }}</div>
            <div class="is-size-7 has-text-grey">{{ item.email }}</div>
          </div>
        </div>
      </template>
    </SearchableSelect>

    <!-- Hidden Dropdown Menu (positioned like SearchableSelect) -->
    <div v-show="isOpen" class="user-dropdown" ref="dropdown">
      <div class="dropdown-content">
        <!-- Search Input -->
        <div class="search-item">
          <p class="control has-icons-left">
            <input
              type="text"
              class="input is-small"
              placeholder="Search users..."
              v-model="searchQuery"
              @click.stop
              ref="searchInput"
            />
            <span class="icon is-small is-left">
              <font-awesome-icon :icon="['fas', 'search']" />
            </span>
          </p>
        </div>

        <!-- Clear Option -->
        <a v-if="clearable && selectedUser" class="dropdown-item" @click="clearSelection">
          <div class="is-flex is-align-items-center">
            <div class="user-list-avatar mr-3">
              <div class="avatar-clear-placeholder">
                <font-awesome-icon :icon="['fal', 'times']" />
              </div>
            </div>
            <span class="has-text-grey">Clear assignment</span>
          </div>
        </a>
        
        <hr v-if="clearable && selectedUser" class="dropdown-divider" />

        <!-- Grouped User Lists (when includeAdmins is true) -->
        <template v-if="showGroupedSections">
          <!-- Admin Users Section -->
          <template v-if="filteredAdminUsers.length > 0">
            <div class="dropdown-item section-header">
              <span class="icon is-small mr-2 has-text-primary">
                <font-awesome-icon :icon="['fas', 'shield-halved']" />
              </span>
              <span class="has-text-weight-semibold is-size-7">TEAM</span>
            </div>
            <a
              v-for="user in filteredAdminUsers"
              :key="user.id"
              class="dropdown-item"
              :class="{ 'is-active': user.id === internalValue }"
              @click="selectUser(user)"
            >
              <div class="is-flex is-align-items-center">
                <div class="user-list-avatar mr-3">
                  <BunnyImage
                    :path="user.profile?.avatarPath || user.avatarPath"
                    :fallback="user.profile?.avatar || user.avatar"
                    :alt="user.name"
                    img-class="avatar-image"
                  >
                    <template #placeholder>
                      <div class="avatar-initials" :style="{ backgroundColor: getUserColor(user.email) }">
                        {{ getUserInitials(user.name || user.email) }}
                      </div>
                    </template>
                  </BunnyImage>
                </div>
                <div class="is-flex-grow-1">
                  <div class="has-text-weight-medium">{{ user.name || 'Unnamed User' }}</div>
                  <div class="is-size-7 has-text-grey">{{ user.email }}</div>
                  <div v-if="warningsMap[user.id]" class="is-size-7 has-text-warning-dark">
                    <font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="mr-1" />{{ warningsMap[user.id] }}
                  </div>
                </div>
                <span v-if="user.id === internalValue" class="icon has-text-primary">
                  <font-awesome-icon :icon="['fas', 'check']" />
                </span>
              </div>
            </a>
          </template>

          <!-- Company Users Section -->
          <template v-if="filteredCompanyUsers.length > 0">
            <hr v-if="filteredAdminUsers.length > 0" class="dropdown-divider" />
            <div class="dropdown-item section-header">
              <span class="icon is-small mr-2 has-text-info">
                <font-awesome-icon :icon="['fas', 'building']" />
              </span>
              <span class="has-text-weight-semibold is-size-7">COMPANY USERS</span>
            </div>
            <a
              v-for="user in filteredCompanyUsers"
              :key="user.id"
              class="dropdown-item"
              :class="{ 'is-active': user.id === internalValue }"
              @click="selectUser(user)"
            >
              <div class="is-flex is-align-items-center">
                <div class="user-list-avatar mr-3">
                  <BunnyImage
                    :path="user.profile?.avatarPath || user.avatarPath"
                    :fallback="user.profile?.avatar || user.avatar"
                    :alt="user.name"
                    img-class="avatar-image"
                  >
                    <template #placeholder>
                      <div class="avatar-initials" :style="{ backgroundColor: getUserColor(user.email) }">
                        {{ getUserInitials(user.name || user.email) }}
                      </div>
                    </template>
                  </BunnyImage>
                </div>
                <div class="is-flex-grow-1">
                  <div class="has-text-weight-medium">{{ user.name || 'Unnamed User' }}</div>
                  <div class="is-size-7 has-text-grey">{{ user.email }}</div>
                  <div v-if="warningsMap[user.id]" class="is-size-7 has-text-warning-dark">
                    <font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="mr-1" />{{ warningsMap[user.id] }}
                  </div>
                </div>
                <span v-if="user.id === internalValue" class="icon has-text-primary">
                  <font-awesome-icon :icon="['fas', 'check']" />
                </span>
              </div>
            </a>
          </template>

          <!-- No Results for grouped -->
          <div v-if="filteredAdminUsers.length === 0 && filteredCompanyUsers.length === 0" class="dropdown-item">
            <p class="has-text-grey">No users found</p>
          </div>
        </template>

        <!-- Standard User List (non-grouped) -->
        <template v-else>
          <a
            v-for="user in filteredUsers"
            :key="user.id"
            class="dropdown-item"
            :class="{ 'is-active': user.id === internalValue }"
            @click="selectUser(user)"
          >
            <div class="is-flex is-align-items-center">
              <!-- User Avatar -->
              <div class="user-list-avatar mr-3">
                <BunnyImage
                  :path="user.profile?.avatarPath || user.avatarPath"
                  :fallback="user.profile?.avatar || user.avatar"
                  :alt="user.name"
                  img-class="avatar-image"
                >
                  <template #placeholder>
                    <div class="avatar-initials" :style="{ backgroundColor: getUserColor(user.email) }">
                      {{ getUserInitials(user.name || user.email) }}
                    </div>
                  </template>
                </BunnyImage>
              </div>

              <!-- User Info -->
              <div class="is-flex-grow-1">
                <div class="has-text-weight-medium">{{ user.name || 'Unnamed User' }}</div>
                <div class="is-size-7 has-text-grey">{{ user.email }}</div>
                <div v-if="warningsMap[user.id]" class="is-size-7 has-text-warning-dark">
                  <font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="mr-1" />{{ warningsMap[user.id] }}
                </div>
              </div>

              <!-- Selected Indicator -->
              <span v-if="user.id === internalValue" class="icon has-text-primary">
                <font-awesome-icon :icon="['fas', 'check']" />
              </span>
            </div>
          </a>

          <!-- No Results -->
          <div v-if="filteredUsers.length === 0" class="dropdown-item">
            <p class="has-text-grey">No users found</p>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue';
import { useUserAuthStore } from '@/stores/userAuthStore';
import SearchableSelect from '@/components/common/SearchableSelect.vue';
import BunnyImage from '@/components/common/BunnyImage.vue';
import { logger } from '@/services/loggerService';
import { userService } from '@/services/userService';

const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: 'Assign user'
  },
  disabled: {
    type: Boolean,
    default: false
  },
  size: {
    type: String,
    default: 'medium',
    validator: (value) => ['small', 'medium', 'large'].includes(value)
  },
  clearable: {
    type: Boolean,
    default: true
  },
  locked: {
    type: Boolean,
    default: false
  },
  // A caller-supplied identity to render instead of looking modelValue up —
  // used for synthetic rows (e.g. an agency shown as one subscriber).
  displayUser: {
    type: Object,
    default: null
  },
  lockedTitle: {
    type: String,
    default: 'Admin - cannot be removed'
  },
  adminOnly: {
    type: Boolean,
    default: true
  },
  includeAdmins: {
    type: Boolean,
    default: false
  },
  companyId: {
    type: String,
    default: null
  },
  warning: {
    type: String,
    default: ''
  },
  warningsMap: {
    type: Object,
    default: () => ({})
  }
});

const emit = defineEmits(['update:modelValue', 'select', 'clear']);

// Stores
const userAuthStore = useUserAuthStore();

// State
const isOpen = ref(false);
const searchQuery = ref('');
const showTooltip = ref(false);
const searchInput = ref(null);
const dropdown = ref(null);
const searchableSelect = ref(null);
const adminUsers = ref([]);
const companyUsersList = ref([]);
const adminUsersList = ref([]);
const loadingUsers = ref(false);
const fallbackUser = ref(null); // For displaying users not in the main list

// Internal value for two-way binding
const internalValue = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
});

// Load users on mount
async function loadUsers() {
  loadingUsers.value = true;
  try {
    if (props.companyId && props.includeAdmins) {
      // Load both company users AND admin/super users (for admin project view)
      const [companyUsers, admins] = await Promise.all([
        userService.getUsersByCompany(props.companyId),
        userService.getAdminUsers()
      ]);

      // Store separately for grouped display
      companyUsersList.value = companyUsers;
      // Filter out admins who are also company users to avoid showing them twice
      const companyUserIds = new Set(companyUsers.map(u => u.id));
      adminUsersList.value = admins.filter(u => !companyUserIds.has(u.id));

      // Merge users for backward compatibility (selectedUser lookup, etc.)
      const userMap = new Map();
      companyUsers.forEach(u => userMap.set(u.id, u));
      admins.forEach(u => {
        if (!userMap.has(u.id)) {
          userMap.set(u.id, u);
        }
      });
      adminUsers.value = Array.from(userMap.values());

      logger.info('[UserAvatarSelect] Company users + admins loaded from Firestore', {
        count: adminUsers.value.length,
        companyId: props.companyId,
        companyUsersCount: companyUsers.length,
        adminsCount: admins.length
      });
    } else if (props.companyId) {
      // Load users for specific company only
      const users = await userService.getUsersByCompany(props.companyId);
      adminUsers.value = users;

      logger.info('[UserAvatarSelect] Company users loaded from Firestore', {
        count: users.length,
        companyId: props.companyId,
      });
    } else if (props.adminOnly) {
      // Query Firestore directly for admin/super users
      const users = await userService.getAdminUsers();
      adminUsers.value = users;

      logger.info('[UserAvatarSelect] Admin users loaded from Firestore', {
        count: users.length,
      });
    } else {
      // Load all users when adminOnly is false
      const users = await userService.getAllUsersWithProfiles();
      adminUsers.value = users;

      logger.info('[UserAvatarSelect] All users loaded from Firestore', {
        count: users.length,
      });
    }
  } catch (error) {
    logger.error('[UserAvatarSelect] Failed to load users', {
      error: error.message,
      adminOnly: props.adminOnly,
      companyId: props.companyId
    });
    adminUsers.value = [];
  } finally {
    loadingUsers.value = false;
  }
}

// Re-fetch users when companyId changes (e.g. prop arrives after async project load)
watch(() => props.companyId, (newId, oldId) => {
  if (newId && newId !== oldId) {
    loadUsers();
  }
});

onMounted(async () => {
  try {
    logger.info('[UserAvatarSelect] Component mounted', {
      isAdmin: userAuthStore.isAdmin,
      currentUserType: userAuthStore.user.type,
      adminOnly: props.adminOnly,
      companyId: props.companyId
    });
    await loadUsers();
  } catch (error) {
    logger.error('[UserAvatarSelect] Failed in onMounted', { error: error.message });
  }
});

// Watch for model value changes to fetch fallback user if needed
watch(() => props.modelValue, async (newValue) => {
  if (!newValue || loadingUsers.value) return;

  // Check if user is already in the available list
  const found = adminUsers.value.find(user => user.id === newValue);
  if (found) {
    fallbackUser.value = null;
    return;
  }

  // User not in list - fetch them directly for display purposes
  try {
    const user = await userService.getUser(newValue);
    if (user) {
      fallbackUser.value = {
        id: user.id || newValue,
        uid: user.id || newValue,
        email: user.profile?.email || user.email || '',
        name: `${user.profile?.firstName || ''} ${user.profile?.lastName || ''}`.trim() || user.name || '',
        avatar: user.profile?.avatar || user.avatar || '',
        profile: user.profile
      };
      logger.info('[UserAvatarSelect] Fetched fallback user for display', {
        userId: newValue,
        userName: fallbackUser.value.name
      });
    }
  } catch (error) {
    logger.warn('[UserAvatarSelect] Could not fetch fallback user', { userId: newValue, error: error.message });
  }
}, { immediate: true });

// Computed properties
const availableUsers = computed(() => {
  // Return the loaded users (either admin-only or all users)
  logger.debug('[UserAvatarSelect] Using loaded users', {
    count: adminUsers.value.length,
    adminOnly: props.adminOnly
  });
  return adminUsers.value;
});

const filteredUsers = computed(() => {
  const result = !searchQuery.value ? availableUsers.value : availableUsers.value.filter(user => {
    const name = (user.name || '').toLowerCase();
    const email = (user.email || '').toLowerCase();
    const query = searchQuery.value.toLowerCase();
    return name.includes(query) || email.includes(query);
  });

  logger.debug('[UserAvatarSelect] Filtered users result', {
    searchQuery: searchQuery.value,
    resultCount: result.length,
    availableCount: availableUsers.value.length
  });

  return result;
});

// Check if we should show grouped sections
const showGroupedSections = computed(() => {
  return props.companyId && props.includeAdmins && (adminUsersList.value.length > 0 || companyUsersList.value.length > 0);
});

// Filtered company users
const filteredCompanyUsers = computed(() => {
  if (!searchQuery.value) return companyUsersList.value;
  const query = searchQuery.value.toLowerCase();
  return companyUsersList.value.filter(user => {
    const name = (user.name || '').toLowerCase();
    const email = (user.email || '').toLowerCase();
    return name.includes(query) || email.includes(query);
  });
});

// Filtered admin users
const filteredAdminUsers = computed(() => {
  if (!searchQuery.value) return adminUsersList.value;
  const query = searchQuery.value.toLowerCase();
  return adminUsersList.value.filter(user => {
    const name = (user.name || '').toLowerCase();
    const email = (user.email || '').toLowerCase();
    return name.includes(query) || email.includes(query);
  });
});

const selectedUser = computed(() => {
  // A caller-supplied identity wins outright — no lookup, no fallback fetch.
  if (props.displayUser) return props.displayUser;
  if (!internalValue.value) return null;
  // First try to find in the loaded users list
  const found = availableUsers.value.find(user => user.id === internalValue.value);
  if (found) return found;
  // Fall back to separately fetched user (for displaying users not in dropdown list)
  if (fallbackUser.value?.id === internalValue.value) return fallbackUser.value;
  return null;
});

const avatarSizeClass = computed(() => {
  return `is-${props.size}`;
});

// Methods
function toggleDropdown() {
  if (props.disabled) return;
  
  isOpen.value = !isOpen.value;
  
  if (isOpen.value) {
    nextTick(() => {
      if (searchInput.value) {
        searchInput.value.focus();
      }
    });
  }
}

function selectUser(user) {
  internalValue.value = user.id;
  emit('select', user);
  closeDropdown();
  logger.info('[UserAvatarSelect] User selected', { userId: user.id, userName: user.name });
}

function clearSelection() {
  internalValue.value = '';
  emit('clear');
  emit('select', null);
  closeDropdown();
  logger.info('[UserAvatarSelect] Selection cleared');
}

function closeDropdown() {
  isOpen.value = false;
  searchQuery.value = '';
}

function handleUserSelect(user) {
  if (user) {
    selectUser(user);
  }
}

// Close dropdown when clicking outside
function handleClickOutside(event) {
  if (!dropdown.value?.contains(event.target) && !event.target.closest('.user-avatar-select')) {
    closeDropdown();
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});

// Helper functions
function getUserInitials(name) {
  if (!name) return '?';
  
  const words = name.trim().split(' ');
  if (words.length === 1) {
    return words[0].charAt(0).toUpperCase();
  }
  
  return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
}

function getUserColor(email) {
  // Generate a consistent color based on email
  const colors = [
    '#3273dc', '#23d160', '#ff3860', '#ffdd57', '#209cee',
    '#363636', '#00d1b2', '#485fc7', '#fa7c91', '#f14668'
  ];
  
  if (!email) return colors[0];
  
  let hash = 0;
  for (let i = 0; i < email.length; i++) {
    hash = email.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  return colors[Math.abs(hash) % colors.length];
}

// Helper function to fetch and add missing user
async function fetchMissingUser(userId) {
  if (!userId) return;

  // Check if user already exists
  if (adminUsers.value.find(u => u.id === userId)) {
    return;
  }

  logger.info('[UserAvatarSelect] User ID not found in available users, fetching', {
    userId: userId,
    availableCount: availableUsers.value.length
  });

  try {
    const user = await userService.getUser(userId);
    if (user) {
      // Transform to match expected format with name from profile
      const userWithName = {
        ...user,
        name: user.profile?.firstName && user.profile?.lastName
          ? `${user.profile.firstName} ${user.profile.lastName}`.trim()
          : user.profile?.email || user.email,
        email: user.profile?.email || user.email,
        avatar: user.profile?.avatar
      };
      adminUsers.value = [...adminUsers.value, userWithName];
      logger.info('[UserAvatarSelect] Missing user fetched and added', {
        userId: userWithName.id,
        userName: userWithName.name,
        userEmail: userWithName.email
      });
    }
  } catch (error) {
    logger.error('[UserAvatarSelect] Failed to fetch missing user', {
      userId: userId,
      error: error.message
    });
  }
}

// Watch for external value changes and fetch missing users
watch(() => props.modelValue, async (newValue) => {
  if (newValue && selectedUser.value) {
    logger.debug('[UserAvatarSelect] External value changed', {
      userId: newValue,
      userName: selectedUser.value.name
    });
  } else if (newValue && !selectedUser.value) {
    // User ID exists but not in availableUsers - fetch it
    await fetchMissingUser(newValue);
  }
}, { immediate: true });

// Watch for when users finish loading - check if we need to fetch the selected user
watch(() => loadingUsers.value, async (isLoading) => {
  if (!isLoading && props.modelValue && !selectedUser.value) {
    // Users finished loading but selected user still not found - fetch it
    logger.info('[UserAvatarSelect] Users loaded but selected user not found, fetching', {
      userId: props.modelValue
    });
    await fetchMissingUser(props.modelValue);
  }
});
</script>

<style scoped lang="scss">
.user-avatar-select {
  position: relative;
  display: inline-block;
}

.avatar-button {
  position: relative;
  cursor: pointer;
  
  &.is-disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
  
  &:hover:not(.is-disabled) {
    .avatar-placeholder {
      border-color: var(--bulma-primary);
      background-color: var(--bulma-primary-light);
    }
    
    .user-avatar {
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    }
  }
}

.user-avatar, .avatar-placeholder {
  position: relative;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  box-sizing: border-box;
  flex-shrink: 0;

  &.is-small {
    width: 32px;
    height: 32px;
  }

  &.is-medium {
    width: 40px;
    height: 40px;
  }

  &.is-large {
    width: 48px;
    height: 48px;
  }
}

.avatar-placeholder {
  border: 2px dashed var(--bulma-border);
  background-color: var(--bulma-scheme-main-ter);
  color: var(--bulma-text-weak);

  .placeholder-icon {
    font-size: 0.875rem;
  }
}

.user-avatar {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  position: relative;

  // Ensure consistent sizing for both image and initials
  .avatar-image,
  .avatar-initials {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    overflow: hidden;
  }

  .avatar-image {
    object-fit: cover;
    border: 2px solid var(--bulma-scheme-main);
  }

  .avatar-initials {
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: 600;
    font-size: 0.875rem;
    border: 2px solid var(--bulma-scheme-main);
    box-sizing: border-box;
  }
}

.avatar-clear {
  position: absolute;
  top: -4px;
  right: -4px;
  width: 18px;
  height: 18px;
  background: var(--bulma-danger);
  color: white;
  border: none;
  border-radius: 50%;
  font-size: 0.6rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: var(--bulma-danger-dark);
    transform: scale(1.1);
  }
}

.avatar-locked {
  position: absolute;
  top: -4px;
  right: -4px;
  width: 18px;
  height: 18px;
  background: var(--bulma-grey);
  color: white;
  border: none;
  border-radius: 50%;
  font-size: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.avatar-warning {
  position: absolute;
  bottom: -3px;
  right: -3px;
  width: 16px;
  height: 16px;
  background: var(--bulma-warning, #ffdd57);
  color: rgba(0, 0, 0, 0.7);
  border: 2px solid white;
  border-radius: 50%;
  font-size: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
}

.avatar-tooltip {
  position: absolute;
  bottom: -35px;
  // Left-aligned, not centred: these avatars sit at the left edge of a sidebar
  // that clips its overflow, and a centred tooltip loses its first word there.
  left: 0;
  background: var(--bulma-dark);
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  white-space: nowrap;
  z-index: 1000;
  pointer-events: none;
  
  &::before {
    content: '';
    position: absolute;
    top: -4px;
    left: 12px;
    border-left: 4px solid transparent;
    border-right: 4px solid transparent;
    border-bottom: 4px solid var(--bulma-dark);
  }
}

.user-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 40;
  margin-top: 0.25rem;
  min-width: 280px;
}

.dropdown-content {
  background: white;
  border-radius: 4px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  max-height: 300px;
  overflow-y: auto;
  position: relative;
  z-index: 40;
}

.search-item {
  padding: 0.5rem;
  border-bottom: 1px solid var(--bulma-border-weak);
  position: sticky;
  top: 0;
  background: white;
  z-index: 1;
}

.dropdown-item {
  display: block;
  padding: 0.75rem 1rem;
  cursor: pointer;

  &:hover {
    background-color: var(--bulma-scheme-main-ter);
  }

  &.is-active {
    background-color: hsl(
      var(--bulma-primary-h),
      var(--bulma-primary-s),
      96%
    );
    border-left: 3px solid var(--bulma-primary);
  }

  &.section-header {
    cursor: default;
    padding: 0.5rem 1rem;
    background-color: var(--bulma-scheme-main-bis);
    display: flex;
    align-items: center;

    &:hover {
      background-color: var(--bulma-scheme-main-bis);
    }
  }
}

.dropdown-divider {
  margin: 0.5rem 0;
  background-color: var(--bulma-border-weak);
  border: none;
  height: 1px;
}

.user-list-avatar {
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  position: relative;
  border-radius: 50%;
  overflow: hidden;

  .avatar-image,
  .avatar-initials {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: 50%;
  }

  .avatar-image {
    object-fit: cover;
    border: 2px solid var(--bulma-scheme-main);
  }

  .avatar-initials {
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: 600;
    font-size: 0.875rem;
    border: 2px solid var(--bulma-scheme-main);
    box-sizing: border-box;
  }
  
  .avatar-clear-placeholder {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--bulma-scheme-main-ter);
    color: var(--bulma-text-weak);
    border: 2px dashed var(--bulma-border);
  }
}

// Ensure proper stacking context
.user-avatar-select {
  position: relative;
  
  // When dropdown is active, ensure it's above siblings
  &:has(.user-dropdown:not([style*="display: none"])) {
    z-index: 30;
  }
}
</style>